import React, { useEffect } from 'react'
import { Link, useSearchParams, useNavigate } from 'react-router-dom'
import Button from 'components/button'
import Input from 'components/input'
import LoginForm, { IFormValues } from 'views/auth/components/LoginForm'
import { useAuth } from 'hooks/auth'
import api, { APIError, ApiErrorCodes } from 'helpers/api'
import { safeLocalStorage } from 'helpers/localStorage'
import { HOME, TWO_FACTOR_SIGNIN, SIGN_UP, FORGOT_PASSWORD, VERIFY_EMAIL } from 'routes'
import { getCurrentUser } from 'helpers/auth'
import { APP_URL, SIGNIN_EMAIL_KEY } from 'app-constants'
import { redirectTo, isSessionEndedReason } from 'helpers/redirect'
import { validateSourceUrl, isValidEmail } from 'helpers/utils'
import { useApplicationStore } from 'hooks/application'

const SignIn: React.FC = () => {
  const { login, startMicrosoftAuth, startGoogleAuth, startSSOAuth } = useAuth()
  const { setSnackbarMessage } = useApplicationStore()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()

  const [error, setError] = React.useState<string | null>(null)
  const [needVerification, setNeedVerification] = React.useState(false)
  const [email, setEmail] = React.useState<string>('')
  const [allowedMethods, setAllowedMethods] = React.useState<string[] | null>(null)
  const [loadingMethods, setLoadingMethods] = React.useState<boolean>(false)
  const [emailError, setEmailError] = React.useState<boolean>(false)

  const showLoginMethods = allowedMethods !== null

  useEffect(() => {
    document.title = 'Morta | Sign In'
  }, [])

  useEffect(() => {
    // A stored session carries on, even here on a page opened because one ended: the stored session is shared
    // by every tab, and only the server's 401 ends it (see endRejectedSession). Clearing it here signed out
    // every other tab whenever an old sign-in tab was reopened; a dead one is cleared by its first request.
    if (getCurrentUser()) {
      navigate(HOME)
      return
    }
    if (isSessionEndedReason(searchParams.get('reason'))) {
      setSnackbarMessage({
        status: 'warning',
        message: 'Your session has timed out. Please sign in again to continue.'
      })
    }
  }, [searchParams, navigate])

  useEffect(() => {
    const checkStoredSigninMethod = async () => {
      const storedEmail = safeLocalStorage.getItem(SIGNIN_EMAIL_KEY)

      if (storedEmail && isValidEmail(storedEmail)) {
        setLoadingMethods(true)
        setError(null)

        try {
          const response = await api.getAllowedLoginMethods(storedEmail)
          const methods = response.data.allowedMethods

          // Only auto-show if organization has enforced login (single method)
          if (methods.length > 0) {
            setEmail(storedEmail)
            setAllowedMethods(methods)
          } else {
            safeLocalStorage.removeItem(SIGNIN_EMAIL_KEY)
          }
        } catch (e) {
          safeLocalStorage.removeItem(SIGNIN_EMAIL_KEY)
        }
        setLoadingMethods(false)
      }
    }

    checkStoredSigninMethod()
  }, [])

  const handleSubmit = async (values: IFormValues) => {
    if (values.email && values.password) {
      try {
        setNeedVerification(false)
        await login(values.email, values.password)
        safeLocalStorage.setItem(SIGNIN_EMAIL_KEY, values.email.toLowerCase())
        navigate(redirectTo(HOME))
      } catch (e) {
        if (e instanceof APIError) {
          if (e.code === ApiErrorCodes.ACCOUNT_NOT_VERIFIED) {
            setNeedVerification(true)
          } else if (e.code === ApiErrorCodes.TWO_FACTOR_REQUIRED) {
            const response = await api.getTempUserToken(values.email, values.password)
            navigate(`${TWO_FACTOR_SIGNIN}?loginToken=${response.data.code}`)
          } else {
            const message = e.message || "Morta couldn't sign you in. Try again."
            const allMessages = e.errors
              ? e.errors.map((err) => (Array.isArray(err.messages) ? err.messages.join(', ') : '')).join(' ')
              : ''
            const errorMessage = allMessages || message
            setError(errorMessage)
          }
        }
      }
    }
  }

  const handleEmailSubmit = async () => {
    if (!isValidEmail(email)) {
      setEmailError(true)
      return
    }
    setLoadingMethods(true)
    setError(null)

    try {
      const response = await api.getAllowedLoginMethods(email)
      setAllowedMethods(response.data.allowedMethods)
    } catch (e) {
      if (e instanceof APIError) {
        const message = e.message || "Morta couldn't sign you in. Try again."
        const allMessages = e.errors
          ? e.errors.map((err) => (Array.isArray(err.messages) ? err.messages.join(', ') : '')).join(' ')
          : ''
        const errorMessage = allMessages || message
        setError(errorMessage)
      }

      setAllowedMethods(null)
    }
    setLoadingMethods(false)
  }

  const handleBackToEmail = () => {
    safeLocalStorage.removeItem(SIGNIN_EMAIL_KEY)
    setAllowedMethods(null)
    setEmail('')
    setEmailError(false)
    setError(null)
  }

  const handleSignUpWithProvider = async (name: string) => {
    switch (name) {
      case 'microsoft':
        startMicrosoftAuth()
        break
      case 'google':
        startGoogleAuth()
        break
      case 'sso':
        startSSOAuth(email)
        break
      default:
    }
  }

  return (
    <div className="bg-really-light-blue flex flex-1 items-center justify-center overflow-hidden box-border h-screen w-full px-8">
      <div className="h-fit flex flex-col bg-surface border border-divider-strong p-8 shadow-heavy rounded text-body w-[550px]">
        {!showLoginMethods ? (
          <>
            <div className="flex flex-col gap-4 mb-4">
              <h1 className="mb-4 flex justify-center">Sign into Morta</h1>
              <label htmlFor="email">Enter your email address</label>
            </div>

            <div className="w-full">
              <Input
                id="email"
                name="email"
                type="text"
                data-cy="email-input"
                value={email}
                onChange={(event) => {
                  setEmail(event.target.value)
                  setError(null)
                }}
                onKeyPress={(event) => {
                  if (event.key === 'Enter') {
                    handleEmailSubmit()
                  }
                }}
                autoComplete="email"
              />
              {emailError && <p className="text-red mt-2.5 mb-2.5">This must be a valid email address</p>}
              {error && <p className="text-red mt-2.5 mb-2.5">{error}</p>}
              <Button
                className="mt-5 w-full text-[1rem]"
                onClick={handleEmailSubmit}
                disabled={!email}
                isLoading={loadingMethods}
                data-cy="continue-button"
              >
                Continue
              </Button>
            </div>
            <p className="border-t border-divider mt-5 pt-5">
              Don&#39;t have an account? <Link to={SIGN_UP}>Sign Up</Link>
            </p>
          </>
        ) : (
          <>
            <div className="flex mb-4 flex-col gap-4">
              <h1 className="mb-4 flex justify-center">Sign into Morta using {email}</h1>
            </div>
            <div>
              {allowedMethods.includes('microsoft') && (
                <Button
                  internalType="outline"
                  className="w-full text-base mb-2.5"
                  onClick={() => handleSignUpWithProvider('microsoft')}
                >
                  <img
                    className="mr-2.5"
                    src={validateSourceUrl(`${APP_URL}/assets/images/microsoft.svg`)}
                    alt="microsoft"
                  />
                  Sign in with Microsoft
                </Button>
              )}
              {allowedMethods.includes('google') && (
                <Button
                  internalType="outline"
                  className="w-full text-base mb-2.5"
                  onClick={() => handleSignUpWithProvider('google')}
                >
                  <img className="mr-2.5" src={validateSourceUrl(`${APP_URL}/assets/images/google.svg`)} alt="google" />
                  Sign in with Google
                </Button>
              )}

              {allowedMethods.includes('saml') && (
                <Button
                  internalType="outline"
                  className="w-full text-base mb-2.5"
                  onClick={() => handleSignUpWithProvider('sso')}
                >
                  <img
                    className="mr-2.5"
                    src={validateSourceUrl(`${APP_URL}/assets/images/sso.svg`)}
                    alt="sso"
                    width={25}
                  />
                  Sign in with SSO
                </Button>
              )}

              {allowedMethods.includes('password') && (
                <>
                  {(allowedMethods.includes('google') ||
                    allowedMethods.includes('microsoft') ||
                    allowedMethods.includes('saml')) && <div className="my-5 text-lg">Or continue with email</div>}
                  <LoginForm onSubmit={handleSubmit} initialEmail={email} />
                </>
              )}
            </div>

            {error && <p className="text-red mt-2.5">{error}</p>}

            {needVerification && (
              <div className="mt-4 p-4 bg-surface-sunken">
                This account must be verified first, please check your email, or{' '}
                <Link to={VERIFY_EMAIL}>send an email verification code</Link>
              </div>
            )}

            <br />

            {allowedMethods.includes('password') && (
              <div className="mb-2">
                <Link to={FORGOT_PASSWORD}>Forgot Password?</Link>
              </div>
            )}

            <Link to="#" onClick={handleBackToEmail} className="pt-[10px] cursor-pointer text-left">
              Sign in with a different email address
            </Link>

            <p className="border-t border-divider mt-5 pt-5">
              Don&#39;t have an account? <Link to={SIGN_UP}>Sign Up</Link>
            </p>
          </>
        )}
      </div>
    </div>
  )
}

export default SignIn
